import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import sketchConversation from "@/assets/sketch-conversation.png";
import sketchResearch from "@/assets/sketch-research.png";
import sketchImplementation from "@/assets/sketch-implementation.png";

const JourneyPreview = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });

  const steps = [
    {
      image: sketchConversation,
      title: t('journey.step.1.title'),
      desc: t('journey.step.1.desc'),
      number: "01"
    },
    {
      image: sketchResearch,
      title: t('journey.step.2.title'),
      desc: t('journey.step.2.desc'),
      number: "02"
    },
    {
      image: sketchImplementation,
      title: t('journey.step.3.title'),
      desc: t('journey.step.3.desc'),
      number: "03"
    }
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="werkwijze"
      className="py-24 bg-muted/30"
      aria-labelledby="journey-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2
              id="journey-heading"
              className="text-4xl md:text-5xl font-bold mb-4 text-foreground"
            >
              {t('journey.title')}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t('journey.subtitle')}
            </p>
          </div>

          {/* Visual journey - horizontal on desktop, vertical on mobile */}
          <div
            className={`relative transition-all duration-700 ease-out delay-150 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Connection line - desktop */}
            <div
              className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary/50 -translate-y-1/2 rounded-full"
              aria-hidden="true"
            />

            {/* Steps */}
            <div className="grid md:grid-cols-3 gap-8 md:gap-4 relative pb-16">
            {steps.map((step, index) => {
              return (
                <div
                  key={index}
                  className="flex flex-col items-center text-center group relative"
                  style={{ transitionDelay: `${200 + index * 150}ms` }}
                >
                  {/* Hover description tooltip - positioned above the icon */}
                  <div className="absolute bottom-full mb-4 left-1/2 -translate-x-1/2 w-64 px-4 py-3 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-20 pointer-events-none">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.desc}
                    </p>
                    {/* Arrow pointing down */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-border" />
                    <div className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-card" />
                  </div>

                  {/* Circle with sketch image */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 rounded-2xl bg-white border-4 border-primary/20 flex items-center justify-center shadow-lg group-hover:border-accent transition-colors duration-300 group-hover:shadow-xl cursor-pointer overflow-hidden p-2">
                      <img 
                        src={step.image} 
                        alt={step.title} 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    {/* Number badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm font-bold shadow-md">
                      {step.number}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-card-foreground">
                    {step.title}
                  </h3>
                </div>
              );
            })}
            </div>
          </div>

          {/* Link to full approach page */}
          <div
            className={`text-center mt-12 transition-all duration-700 ease-out delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Link
              to="/werkwijze"
              className="inline-flex items-center gap-2 text-lg font-medium text-accent hover:text-accent/80 transition-colors group"
            >
              {t('journey.cta')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneyPreview;
